import * as Sentry from '@sentry/nuxt';

interface FormField {
  id: string;
  fieldType: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
}

interface FormErrorContext {
  formId?: string;
  component: string;
  action: string;
  webhookUrl?: string;
  fields?: FormField[];
  formData?: Record<string, any>;
  validationState?: {
    hasErrors: boolean;
    touchedFields: string[];
    errorFields: string[];
  };
  userAgent?: string;
  url?: string;
  [key: string]: any;
}

export function useSentry() {
  /**
   * Capture form-related errors with comprehensive context
   */
  const captureFormError = (
    error: Error | unknown,
    context: FormErrorContext
  ) => {
    const {
      formId,
      component,
      action,
      webhookUrl,
      fields = [],
      formData = {},
      validationState,
      userAgent,
      url,
      ...extraContext
    } = context;

    // Prepare tags for filtering and searching
    const tags: Record<string, string> = {
      context: 'form-error',
      component,
      action,
      formId: formId || 'unknown',
      webhookUrl: webhookUrl ? 'configured' : 'missing',
      formType: fields.length > 1 ? 'multi-field' : 'single-field',
      primaryFieldType: fields[0]?.fieldType || 'unknown'
    };

    // Prepare extra data (no sensitive information)
    const extra: Record<string, any> = {
      formData: '***REDACTED***', // Never log actual form data
      fieldCount: fields.length,
      fieldTypes: fields.map((f) => f.fieldType),
      fieldConfiguration: fields.map((f) => ({
        id: f.id,
        type: f.fieldType,
        required: f.required,
        hasLabel: !!f.label,
        hasPlaceholder: !!f.placeholder,
        hasValue: !!formData[f.id]
      })),
      validationState,
      webhookUrlStatus: webhookUrl ? '[configured]' : '[missing]',
      userAgent: userAgent || navigator?.userAgent,
      url: url || window?.location?.href,
      timestamp: new Date().toISOString(),
      ...extraContext
    };

    // Capture the exception
    Sentry.captureException(error, {
      tags,
      extra,
      user: {
        id: `form_${formId || 'unknown'}`,
        segment: 'form_user'
      }
    });
  };

  /**
   * Capture general application errors with context
   */
  const captureError = (
    error: Error | unknown,
    context: {
      component: string;
      action: string;
      tags?: Record<string, string>;
      extra?: Record<string, any>;
      user?: Record<string, any>;
    }
  ) => {
    const { component, action, tags = {}, extra = {}, user = {} } = context;

    Sentry.captureException(error, {
      tags: {
        context: 'application-error',
        component,
        action,
        ...tags
      },
      extra: {
        timestamp: new Date().toISOString(),
        userAgent: navigator?.userAgent,
        url: window?.location?.href,
        ...extra
      },
      user
    });
  };

  /**
   * Add breadcrumb for tracking user actions
   */
  const addBreadcrumb = (
    messageOrData:
      | string
      | { message: string; category?: string; level?: string },
    category: string = 'user-action',
    level: 'info' | 'warning' | 'error' = 'info',
    data?: Record<string, any>
  ) => {
    if (typeof messageOrData === 'string') {
      Sentry.addBreadcrumb({
        message: messageOrData,
        category,
        level,
        data,
        timestamp: Date.now() / 1000
      });
    } else {
      // Handle object format
      Sentry.addBreadcrumb({
        message: messageOrData.message,
        category: messageOrData.category || category,
        level: (messageOrData.level as any) || level,
        data,
        timestamp: Date.now() / 1000
      });
    }
  };

  /**
   * Set user context
   */
  const setUser = (user: {
    id?: string;
    email?: string;
    segment?: string;
    [key: string]: any;
  }) => {
    Sentry.setUser(user);
  };

  /**
   * Set additional context
   */
  const setContext = (key: string, context: Record<string, any>) => {
    Sentry.setContext(key, context);
  };

  /**
   * Direct wrapper for Sentry.captureException (legacy compatibility)
   */
  const captureException = (
    error: Error | unknown,
    context?: {
      context?: string;
      [key: string]: any;
    }
  ) => {
    const { context: contextName, ...extra } = context || {};

    Sentry.captureException(error, {
      tags: {
        context: contextName || 'legacy'
      },
      extra: {
        timestamp: new Date().toISOString(),
        userAgent: navigator?.userAgent,
        url: window?.location?.href,
        ...extra
      }
    });
  };

  /**
   * Direct wrapper for Sentry.captureMessage (legacy compatibility)
   */
  const captureMessage = (
    message: string,
    level: 'info' | 'warning' | 'error' = 'info'
  ) => {
    Sentry.captureMessage(message, level);
  };

  /**
   * Helper to track form interactions
   */
  const trackFormInteraction = (
    action:
      | 'submit'
      | 'error'
      | 'validation_error'
      | 'field_focus'
      | 'field_blur',
    formId: string,
    component: string,
    data?: Record<string, any>
  ) => {
    addBreadcrumb(
      `Form ${action}: ${formId}`,
      'form-interaction',
      action === 'error' || action === 'validation_error' ? 'warning' : 'info',
      {
        formId,
        component,
        action,
        ...data
      }
    );
  };

  return {
    captureFormError,
    captureError,
    captureException,
    captureMessage,
    addBreadcrumb,
    setUser,
    setContext,
    trackFormInteraction
  };
}
